<script setup lang="ts">
  import { computed, provide, ref } from 'vue'
  import {
    cn,
    Modal,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    Sidebar,
    SidebarContent,
    SidebarHeader,
    SidebarInset,
    SidebarProvider,
    useModalStack
  } from '@gat-ship/vue-components'
  import {
    IconAccessControl,
    IconClose,
    IconCompany,
    IconCurrency,
    IconCustomFields,
    IconEstimatedTime,
    IconLayers,
    IconMaximize,
    IconMinimize,
    IconNetwork,
    IconSliders,
    IconTenantUsers
  } from '@gat-ship/icons'
  import AdministrationMainMenu from './layouts/main-menu/AdministrationMainMenu.vue'
  import {
    ADMIN_TARGETS,
    adminMenuNavKey,
    createModalAdminMenuNav,
    type AdminPanelKey
  } from './layouts/main-menu/admin-menu-nav'
  import AdministrationPanelAccessRoles from './panels/AdministrationPanelAccessRoles.vue'
  import AdministrationPanelAgencySettings from './panels/AdministrationPanelAgencySettings.vue'
  import AdministrationPanelAgencyStructure from './panels/AdministrationPanelAgencyStructure.vue'
  import AdministrationPanelCompanies from './panels/AdministrationPanelCompanies.vue'
  import AdministrationPanelCurrencies from './panels/AdministrationPanelCurrencies.vue'
  import AdministrationPanelCustomFields from './panels/AdministrationPanelCustomFields.vue'
  import AdministrationPanelEstimatedTimeNames from './panels/AdministrationPanelEstimatedTimeNames.vue'
  import AdministrationPanelPortcallTypes from './panels/AdministrationPanelPortcallTypes.vue'
  import AdministrationPanelTenantUsers from './panels/AdministrationPanelTenantUsers.vue'
  import { useAdministrationModal } from '../composables/useAdministrationModal'
  import { useAdminTabClass } from '../composables/useAdminTabClass'

  // Open-state and the active panel come from this modal's entry in the central
  // ordered stack (the `?modals=` codec owns the URL): `admin:<panel>`. The
  // agency-settings panel's `?nodeId` sub-scope stays as its own param and is
  // cleared atomically on close via the registry's `cleanupQueryKeys` (see
  // register-app-modals).
  const KIND = 'admin'
  const stack = useModalStack()
  const entry = computed(() =>
    stack.entries.value.find((item) => item.kind === KIND)
  )

  // Valid `?modals=admin:<key>` values: every menu destination's slug. The menu
  // and the deep-link share `ADMIN_TARGETS` as the single source of truth.
  const validAdminKeys = new Set<string>(
    Object.values(ADMIN_TARGETS).map((target) => target.key)
  )

  const { toValidAdminKey } = useAdministrationModal()

  function toValidItem(value: string | null | undefined): AdminPanelKey | null {
    return toValidAdminKey(value, validAdminKeys)
  }

  const open = computed({
    get: () => entry.value != null,
    set: (value) => {
      if (!value) stack.close(KIND)
    }
  })

  // An invalid/absent panel falls back to the default without closing the modal.
  const activeItem = computed<AdminPanelKey>({
    get: () => toValidItem(entry.value?.params.panel) ?? 'estimated-time-names',
    set: (item) => {
      if (entry.value) stack.update(KIND, { panel: item })
    }
  })

  // The shared `AdministrationMainMenu` becomes a panel switcher: the modal nav
  // strategy renders items as buttons that drive `activeItem` instead of routes.
  provide(adminMenuNavKey, createModalAdminMenuNav(activeItem))

  // Viewport-filling mode, toggled from the header next to the close button.
  const isFullScreen = ref(false)

  /** Mobile tab-bar item classes. */
  const tabClass = useAdminTabClass(activeItem, 'bar')
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    :max-width="1000"
    full-screen-mobile
    hide-close-button
    :full-screen="isFullScreen"
  >
    <SidebarProvider
      :class="
        cn(
          'h-full min-h-0 w-full overflow-hidden',
          isFullScreen ? 'md:h-full' : 'md:h-[min(800px,85vh)] md:rounded-lg'
        )
      "
    >
      <ResizablePanelGroup
        direction="horizontal"
        auto-save-id="administration-modal-sidebar"
      >
        <ResizablePanel
          class="hidden md:block"
          :default-size="22"
          :min-size="18"
          :max-size="40"
        >
          <Sidebar
            collapsible="none"
            class="bg-muted/50 hidden h-full w-full dark:bg-neutral-800/50 md:flex"
          >
            <SidebarHeader
              class="text-foreground px-5 py-3 text-sm font-semibold"
            >
              Administration
            </SidebarHeader>
            <SidebarContent
              class="flex flex-col items-start gap-1.5 overflow-auto px-5 pb-4 pt-2"
            >
              <AdministrationMainMenu />
            </SidebarContent>
          </Sidebar>
        </ResizablePanel>
        <ResizableHandle class="hidden md:flex" />
        <ResizablePanel class="flex flex-col" :default-size="78">
          <SidebarInset class="overflow-hidden">
            <div class="relative z-10 shrink-0">
              <div class="bg-background flex items-center px-3 py-1.5">
                <div class="min-w-0 flex-1">
                  <span
                    class="text-foreground pl-1 text-sm font-semibold md:hidden"
                  >
                    Administration
                  </span>
                </div>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent hidden shrink-0 cursor-pointer rounded-md p-1.5 transition-colors md:block"
                  @click="isFullScreen = !isFullScreen"
                >
                  <IconMinimize v-if="isFullScreen" class="size-4" />
                  <IconMaximize v-else class="size-4" />
                  <span class="sr-only">
                    {{ isFullScreen ? 'Exit full screen' : 'Full screen' }}
                  </span>
                </button>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer rounded-md p-1.5 transition-colors"
                  @click="open = false"
                >
                  <IconClose class="size-4" />
                  <span class="sr-only">Close</span>
                </button>
              </div>
              <nav
                class="border-border bg-background flex shrink-0 gap-1 overflow-x-auto border-b px-3 md:hidden"
              >
                <button
                  type="button"
                  :class="tabClass('tenant-users')"
                  @click="activeItem = 'tenant-users'"
                >
                  <IconTenantUsers class="size-4" />
                  <span>Tenant Users</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('access-roles')"
                  @click="activeItem = 'access-roles'"
                >
                  <IconAccessControl class="size-4" />
                  <span>Access Roles</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('agency-structure')"
                  @click="activeItem = 'agency-structure'"
                >
                  <IconNetwork class="size-4" />
                  <span>Agency Structure</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('companies')"
                  @click="activeItem = 'companies'"
                >
                  <IconCompany class="size-4" />
                  <span>Companies</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('currencies')"
                  @click="activeItem = 'currencies'"
                >
                  <IconCurrency class="size-4" />
                  <span>Currencies</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('custom-fields')"
                  @click="activeItem = 'custom-fields'"
                >
                  <IconCustomFields class="size-4" />
                  <span>Field Customization</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('estimated-time-names')"
                  @click="activeItem = 'estimated-time-names'"
                >
                  <IconEstimatedTime class="size-4" />
                  <span>Estimated Times</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('portcall-types')"
                  @click="activeItem = 'portcall-types'"
                >
                  <IconLayers class="size-4" />
                  <span>Portcall Type</span>
                </button>
                <button
                  type="button"
                  :class="tabClass('agency-settings')"
                  @click="activeItem = 'agency-settings'"
                >
                  <IconSliders class="size-4" />
                  <span>Agency Settings</span>
                </button>
              </nav>
              <div
                class="bg-background pointer-events-none h-3"
                style="
                  mask-image: linear-gradient(to bottom, black, transparent);
                "
              />
            </div>
            <div class="-mt-3 flex flex-1 flex-col overflow-y-auto">
              <AdministrationPanelTenantUsers
                v-if="activeItem === 'tenant-users'"
              />
              <AdministrationPanelAccessRoles
                v-else-if="activeItem === 'access-roles'"
              />
              <AdministrationPanelAgencyStructure
                v-else-if="activeItem === 'agency-structure'"
              />
              <AdministrationPanelCompanies
                v-else-if="activeItem === 'companies'"
              />
              <AdministrationPanelCurrencies
                v-else-if="activeItem === 'currencies'"
              />
              <AdministrationPanelCustomFields
                v-else-if="activeItem === 'custom-fields'"
              />
              <AdministrationPanelEstimatedTimeNames
                v-else-if="activeItem === 'estimated-time-names'"
              />
              <AdministrationPanelPortcallTypes
                v-else-if="activeItem === 'portcall-types'"
              />
              <AdministrationPanelAgencySettings
                v-else-if="activeItem === 'agency-settings'"
              />
              <div
                v-else
                class="text-muted-foreground flex flex-1 items-center justify-center p-8 text-center text-sm"
              >
                This section is not available in the modal yet.
              </div>
            </div>
          </SidebarInset>
        </ResizablePanel>
      </ResizablePanelGroup>
    </SidebarProvider>
  </Modal>
</template>
